import {useState} from 'react';
import {GetStaticProps} from 'next';
import Head from 'next/head';
import {dehydrate, QueryClient} from '@tanstack/react-query';
import {Container, Heading, Box, Grid, Flex} from '@chakra-ui/react';

import {py} from '@eksab/theme';
import {setApiLocale} from '@eksab/api';
import {useTranslate} from '@eksab/i18n';
import {useInfiniteScroll, useLeaguesQuery, useRouterFilter} from '@eksab/hooks';
import {TodayMatches, ExpandingSearch, useTodayMatchesQuery, InfiniteScrollingIndicator} from '@eksab/components';
import {ScrollableFilters, FeaturedArticleCard, ArticleCard} from '@eksab/features/news/components';
import {useInfiniteArticlesQuery} from '@eksab/features/news/hooks';
import {useRegionSettings} from '@eksab/hooks/useRegionSettings';

export default function NewsHome() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const t = useTranslate();
  const [query, setQuery, debouncedQuery] = useRouterFilter({default: '', name: 'query', wait: 500});
  const [, setActiveLeague] = useRouterFilter({default: '', name: 'query'});
  const {data: leagues} = useLeaguesQuery();
  const {hasNextPage, isFetchingNextPage, fetchNextPage, ...articlesQuery} = useInfiniteArticlesQuery({
    query: debouncedQuery || undefined,
  });

  useInfiniteScroll({action: fetchNextPage, condition: !isFetchingNextPage && !!hasNextPage, threshold: 0.5});

  const all = articlesQuery.data?.pages?.flatMap((page) => page.data) ?? [];
  const featuredIndex = all.findIndex((article) => !article.is_premium);
  const featuredArticle = all[featuredIndex];
  const articles = [...all.slice(0, featuredIndex), ...all.slice(featuredIndex + 1)];

  return (
    <>
      <Head>
        <title>{`${t('app.news')} | ${t('app.name')}`}</title>
      </Head>

      <TodayMatches />

      {leagues && (
        <ScrollableFilters items={leagues} onItemClick={({name}) => setActiveLeague(name)} activeItem={query} />
      )}

      <Container as="article" flexDir="column" gap={[4, null, 6]} py={py}>
        <Flex as="section" pos="relative" w="full" justify="space-between" align="center" gap="5">
          <Heading
            as="h1"
            pos={['absolute', null, 'static']}
            insetStart={isSearchOpen ? '-100%' : '0'}
            transition="inset 0.7s ease-out"
          >
            أخر الأخبار
          </Heading>

          <Flex flex={1} justify="flex-end">
            <ExpandingSearch
              isSearchOpen={isSearchOpen}
              setIsSearchOpen={setIsSearchOpen}
              query={query}
              setQuery={setQuery}
              placeholder="ابحث بالدوري أو الفريق أو اللاعب "
            />
          </Flex>
        </Flex>

        <Grid
          as="section"
          templateColumns={[
            'auto',
            'repeat(auto-fill, minmax(18.75rem, 1fr))',
            null,
            'repeat(2, 1fr)',
            'repeat(3, 1fr)',
          ]}
          gap="5"
        >
          {featuredArticle && <FeaturedArticleCard article={featuredArticle} gridColumn={{base: '1/-1', xl: '1/-2'}} />}

          <Box display={{base: 'none', xl: 'initial'}} className="ad" gridColumn={{base: '1', xl: '-2/-1'}}>
            {/* Ad Placeholder */}
          </Box>

          {articles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </Grid>

        {articlesQuery.isFetched && <InfiniteScrollingIndicator isOver={!hasNextPage} />}
      </Container>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({locale}) => {
  const queryClient = new QueryClient();

  setApiLocale(locale);

  await Promise.all([
    queryClient.prefetchInfiniteQuery(useInfiniteArticlesQuery.queryKey({}, false), () =>
      useInfiniteArticlesQuery.queryFn({}, 1, false),
    ),
    queryClient.prefetchQuery([...useLeaguesQuery.queryKey, locale], useLeaguesQuery.queryFn),
    queryClient.prefetchQuery([...useTodayMatchesQuery.queryKey, locale], useTodayMatchesQuery.queryFn),
    queryClient.prefetchQuery([useRegionSettings.queryKey, locale], useRegionSettings.queryFn),
  ]);

  return {
    revalidate: 3 * 60 * 60,
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};
